import { Portal } from "@ark-ui/react/portal";
import { Select, createListCollection } from "@ark-ui/react/select";
import React from "react";
import { LuCheck, LuChevronDown } from "react-icons/lu";
import { useMeasure, useWindowSize } from "react-use";
import { Merge } from "type-fest";

import { css, cx } from "../../styled-system/css";
import { FormFieldInputStyle, SkeletonScreen, TypographyStyle } from "../common";

export type DropDownListProperties = Merge<
  React.ComponentProps<"select">,
  {
    className?: React.ComponentProps<"div">["className"];
  }
>;

export function DropDownList({ children, className, ...properties }: DropDownListProperties) {
  return (
    <div
      className={cx(
        css(FormFieldInputStyle.raw({ border: true }), {
          paddingX: 0,
          paddingY: 0,
          position: "relative",

          _before: {
            content: "''",
            position: "absolute",
            top: "calc(50% - 4px)",
            right: "16px",
            width: "6px",
            height: "6px",
            borderColor: "foreground",
            borderStyle: "solid",
            borderBottomWidth: "1.5px",
            borderRightWidth: "1.5px",
            transform: "rotate(45deg)",
            pointerEvents: "none",
          },
        }),
        className,
      )}
    >
      <select
        {...properties}
        className={css(TypographyStyle.raw({ ellipsis: true }), {
          appearance: "none",
          cursor: "context-menu",
          fontSize: "14px",
          fontWeight: 500,
          lineHeight: 1,
          paddingLeft: "12px",
          paddingRight: "28px",
          paddingY: "12px",
          width: "stretch",
        })}
      >
        {children}
      </select>
    </div>
  );
}

// =============================================================

export type DropDownListOption2 = {
  label: string;
  value: string;
  description?: string;
};

export type DropDownList2Properties = Merge<
  Select.RootProps<DropDownListOption2>,
  {
    children?: never;
    collection?: never;
    loading?: boolean;
    options: ReadonlyArray<DropDownListOption2>;
  }
>;
export function DropDownList2({
  className,
  loading,
  options,
  ...properties
}: DropDownList2Properties) {
  const [reference, { width: elementWidth }] = useMeasure<HTMLDivElement>();
  const { width: windowWidth } = useWindowSize();

  const collection = createListCollection({ items: options });

  return (
    <Select.Root {...properties} className={className} collection={collection}>
      <Select.Control className={css({ width: "stretch" })} ref={reference}>
        <Select.Trigger
          className={css({
            alignItems: "center",
            backgroundColor: "white",
            display: "flex",
            borderColor: "border",
            borderRadius: "4px",
            borderStyle: "solid",
            borderWidth: "1px",
            gap: "8px",
            justifyContent: "space-between",
            paddingLeft: "16px",
            paddingRight: "12px",
            paddingY: "10.5px",
            width: "stretch",
            "&:disabled": { opacity: 0.6 },
          })}
        >
          {loading ? (
            <SkeletonScreen className={css({ flexGrow: 1, height: "14px" })} />
          ) : (
            <Select.ValueText
              className={css({
                flexGrow: 1,
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: 1,
                minWidth: 0,
                overflow: "clip",
                textAlign: "left",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              })}
            />
          )}
          <Select.Indicator>
            <LuChevronDown className={css({ color: "#18181B", height: "16px", width: "auto" })} />
          </Select.Indicator>
        </Select.Trigger>
      </Select.Control>
      <Portal>
        <Select.Positioner>
          <Select.Content
            className={css({
              backgroundColor: "white",
              borderColor: "#E4E4E7",
              borderStyle: "solid",
              borderWidth: "1px",
              boxShadow: "0px 2px 4px -1px #0000000F, 0px 4px 6px -1px #0000001A",
              borderRadius: "6px",
              overflow: "clip",
            })}
            style={{
              maxWidth: (elementWidth > windowWidth / 2 ? elementWidth : windowWidth * 0.8) + "px",
              minWidth: elementWidth + "px",
            }}
          >
            {collection.items.map(function (item) {
              return (
                <Select.Item
                  className={css({
                    alignItems: "center",
                    cursor: "pointer",
                    display: "flex",
                    gap: "8px",
                    padding: "6px 8px",
                    '&[data-state="checked"]': { backgroundColor: "#EFF6FF" },
                    '&[data-state="unchecked"]': { backgroundColor: "white" },
                    '&[data-state="unchecked"]:hover': { backgroundColor: "#FAFCFF" },
                  })}
                  item={item}
                  key={item.value}
                >
                  <Select.ItemText
                    className={css({
                      '&[data-state="checked"]': { opacity: 1 },
                      '&[data-state="unchecked"]': { opacity: 0 },
                    })}
                  >
                    <LuCheck className={css({ color: "#09090B", height: "16px", width: "auto" })} />
                  </Select.ItemText>
                  <Select.ItemText
                    className={css({ display: "flex", flexDirection: "column", gap: "2px" })}
                  >
                    <span
                      className={css({ fontSize: "14px", color: "#09090B", lineHeight: 20 / 14 })}
                    >
                      {item.label}
                    </span>
                    <span
                      className={css({
                        color: "#09090B99",
                        fontSize: "12px",
                        lineHeight: 16 / 12,
                        _empty: { display: "none" },
                      })}
                    >
                      {item.description}
                    </span>
                  </Select.ItemText>
                </Select.Item>
              );
            })}
          </Select.Content>
        </Select.Positioner>
      </Portal>
      <Select.HiddenSelect />
    </Select.Root>
  );
}
